import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HUMUS_FARBEN, HUMUS_STUFEN, HUMUS_VORRAT_STUFEN, HumusStufe } from './humus-stufen';

/**
 * Hilfe zum Humusgehalt einer Substratlage.
 *
 * Vorher stand hier ein Textblock mit sechs Zeilen, dessen Stufen um eins
 * verschoben waren und dessen Spannen nicht zur Anweisung passten. Jetzt die
 * Reihe aus SEA 95, Teil B, Darst. B 3-14 mit beiden Kurzzeichen — das alte aus
 * SEA 74 steht mit da, weil bei der Umkartierung Altformulare gelesen werden.
 *
 * Der Humusvorrat steht in einem eigenen Block: Er hat eine ANDERE Bezugsbasis
 * (Vorrat eines Bodenblocks bis 80 cm in dt/ha statt Gehalt einer Lage in
 * Masse-%). In einer gemeinsamen Tabelle sähe es aus, als wären es zwei Spalten
 * derselben Sache.
 */
@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Humusgehalt — organische Substanz</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p class="einleitung">
				Als organische Substanz zählt alles Brennbare im Boden; die Anweisung nennt sie
				kurz Humus. Genau bestimmen lässt sie sich nur im Labor über den Kohlenstoff:
				<strong>Humus (%) = C (%) × 1,73</strong>. Im Gelände wird die Stufe geschätzt.
			</p>
			<p class="basis">
				Angegeben ist der Gehalt <strong>einer Lage</strong> in Masse-% bei absolut
				trockener Probe.
			</p>

			<table class="table table-sm humus-tabelle">
				<thead>
					<tr>
						<th class="eingabe-spalte">Eingabe</th>
						<th class="code-spalte">SEA 95</th>
						<th class="code-spalte">SEA 74</th>
						<th class="spanne-spalte">Masse-%</th>
						<th>Bezeichnung</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let s of stufen">
						<td class="eingabe-spalte">
							<span class="farbfeld" [style.background]="farbe(s)"></span>
							<span class="wert">{{ s.wert }}</span>
						</td>
						<td class="code-spalte">
							<code *ngIf="s.sea95">{{ s.sea95 }}</code>
							<span *ngIf="!s.sea95" class="leer" title="SEA 95 führt für diese Klasse kein Kurzzeichen">—</span>
						</td>
						<td class="code-spalte alt">
							<span *ngIf="s.sea74" class="ueberstrich" [class.einfach]="s.strich === 'einfach'" [class.doppelt]="s.strich === 'doppelt'">{{ s.sea74 }}</span>
							<span *ngIf="!s.sea74" class="leer">—</span>
						</td>
						<td class="spanne-spalte">{{ s.spanne }}</td>
						<td>{{ s.bezeichnung }}</td>
					</tr>
				</tbody>
			</table>

			<p class="hinweis">
				Die oberste Stufe (Eingabe <strong>7</strong>, über 30 % organische Substanz)
				ist der Humusboden. SEA 95 führt dafür kein eigenes Kurzzeichen; in den alten
				Formularen steht dort ein <span class="ueberstrich einfach">H</span>.
			</p>

			<div class="wirkung">
				<h6>Was die Stufe bewirkt</h6>
				<p class="mb-0">
					Die Stufen <strong>2 bis 6</strong> erhöhen den nutzbaren Wasserspeicher (nWSK):
					Humus hält Wasser. Wie viel je Stufe dazukommt, hängt an der Bodenart und steht
					in der Bodenartentabelle der Listenverwaltung. Die Stufen 0, 1 und 7 bringen
					keinen Zuschlag auf.
				</p>
			</div>

			<div class="vorrat">
				<h6>Humusvorrat — andere Bezugsbasis</h6>
				<p class="basis">
					Diese Reihe wertet den Vorrat eines <strong>ganzen Bodenblocks bis 80 cm Tiefe
					in dt/ha</strong>. Sie hat mit den Stufen oben nichts zu tun und lässt sich
					nicht in sie umrechnen.
				</p>
				<table class="table table-sm vorrat-tabelle">
					<tbody>
						<tr *ngFor="let v of vorratStufen">
							<td class="vorrat-name">{{ v.bezeichnung }}</td>
							<td class="vorrat-spanne">{{ v.spanne }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<p class="quelle">
				Quelle: SEA 95, Teil B, Darst. B 3-14 „Gehalt an organischer Substanz
				(Humusgehalt) in Masse-%", S. 71 (gleichlautend S. 177);
				Humusvorrat S. 145 und S. 177.
			</p>
		</div>
	`,
	styles: [`
		.modal-body {
			padding: 1rem 1.5rem;
		}
		.einleitung {
			margin-bottom: 0.5rem;
		}
		.basis {
			font-size: 0.88rem;
			color: #495057;
			margin-bottom: 1rem;
		}
		.humus-tabelle {
			margin-bottom: 0.75rem;
		}
		.humus-tabelle th {
			background: #f8f9fa;
			font-weight: 600;
			white-space: nowrap;
		}
		.humus-tabelle td,
		.humus-tabelle th {
			vertical-align: middle;
		}
		.eingabe-spalte {
			width: 6.5rem;
			white-space: nowrap;
		}
		.farbfeld {
			display: inline-block;
			width: 1.1rem;
			height: 1.1rem;
			border: 1px solid #adb5bd;
			border-radius: 3px;
			vertical-align: middle;
			margin-right: 0.5rem;
		}
		.wert {
			font-weight: 600;
			font-size: 1rem;
		}
		.code-spalte {
			width: 5rem;
			white-space: nowrap;
		}
		.humus-tabelle code {
			font-size: 1rem;
			color: #212529;
			background: #f1f3f5;
			border-radius: 3px;
			padding: 0.1rem 0.4rem;
		}
		.code-spalte.alt {
			color: #6c757d;
		}
		.spanne-spalte {
			width: 8rem;
			white-space: nowrap;
			text-align: right;
			padding-right: 1.25rem;
		}
		.leer {
			color: #adb5bd;
		}
		.ueberstrich {
			display: inline-block;
			line-height: 1.1;
			font-size: 1rem;
			padding-top: 1px;
		}
		.ueberstrich.einfach {
			border-top: 1px solid currentColor;
		}
		.ueberstrich.doppelt {
			border-top: 3px double currentColor;
		}
		.hinweis {
			font-size: 0.9rem;
			margin-bottom: 1rem;
		}
		.wirkung,
		.vorrat {
			border: 1px solid #dee2e6;
			border-radius: 4px;
			padding: 0.75rem 1rem;
			margin-bottom: 1rem;
			background: #f8f9fa;
		}
		.wirkung h6,
		.vorrat h6 {
			font-weight: 600;
			margin-bottom: 0.5rem;
		}
		.wirkung p,
		.vorrat .basis {
			font-size: 0.9rem;
		}
		.vorrat-tabelle {
			margin-bottom: 0;
			background: #fff;
		}
		.vorrat-name {
			width: 12rem;
		}
		.vorrat-spanne {
			white-space: nowrap;
			text-align: right;
			padding-right: 1.25rem;
		}
		.vorrat-tabelle td {
			font-size: 0.88rem;
		}
		.quelle {
			font-size: 0.82rem;
			color: #6c757d;
			margin-bottom: 0;
		}
	`]
})
export class HumusgehaltInfoModalComponent {
	activeModal = inject(NgbActiveModal);

	stufen = HUMUS_STUFEN;
	vorratStufen = HUMUS_VORRAT_STUFEN;

	/**
	 * Farbtreppe von hell nach dunkel — nur zur Orientierung beim Suchen der
	 * Zeile, keine Aussage über die Bodenfarbe. Dieselbe Reihe färbt das Feld
	 * in der Substrattabelle (HUMUS_FARBEN in humus-stufen.ts).
	 */
	farbe(s: HumusStufe): string {
		return HUMUS_FARBEN[s.wert] ?? '#f8f9fa';
	}
}
